import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowRight,
  Cpu,
  Search,
  CheckCircle2,
  Shield,
  Zap,
  Layers,
  Globe2,
  Terminal,
  Activity,
  Box,
  Fingerprint,
  Play,
  Settings,
  BarChart3,
  Lock,
  Workflow,
  ChevronDown,
  Mail,
  Rocket,
  Users2,
  CreditCard,
  Briefcase,
  ShieldCheck,
  FileText,
  History,
  X,
  Loader2,
  Upload,
} from 'lucide-react';
import { Button, Card, Badge, Input } from '../components/UI';
import { AgentNetwork } from '../components/AgentVisual';
import { HUDOverlay } from '../components/HUD';
import { AdminConsole } from '../components/MissionConsole';
import { AgentCoreVisual } from '../components/AgentCoreVisual';
import { cn } from '../lib/utils';
import { useStore } from '../store/useStore';
import LensVerseLogo from './LenseverseLogo.png';

const MiniHUDWidget = ({ type }: { type: 'chart' | 'pulse' | 'gauge' | 'log' }) => {
  if (type === 'chart') {
    return (
      <div className="h-12 w-full flex items-end gap-0.5 opacity-20 group-hover:opacity-40 transition-opacity">
        {[4, 7, 5, 8, 3, 9, 6, 4, 7, 5].map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 bg-accent rounded-t-sm"
            initial={{ height: 0 }}
            animate={{ height: `${h * 10}%` }}
            transition={{ delay: i * 0.1, duration: 1, repeat: Infinity, repeatType: 'reverse' }}
          />
        ))}
      </div>
    );
  }
  if (type === 'pulse') {
    return (
      <div className="relative h-12 w-full flex items-center justify-center overflow-hidden border-y border-white/5">
        <motion.div
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-transparent via-accent/20 to-transparent"
        />
        <Activity className="w-5 h-5 text-accent opacity-20" />
      </div>
    );
  }
  if (type === 'gauge') {
    return (
      <div className="w-full flex justify-center py-2">
        <div className="w-16 h-16 rounded-full border-2 border-white/5 flex items-center justify-center relative">
          <motion.div
            className="absolute inset-0 border-t-2 border-accent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          />
          <span className="text-[10px] font-black text-accent">99.8%</span>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-black/20 p-2 rounded font-mono text-[8px] space-y-0.5 opacity-40 group-hover:opacity-60 transition-opacity h-12 overflow-hidden">
      <div className="text-accent">agent.ocr_load: 12%</div>
      <div className="text-gold">sync.buffer: ok</div>
      <div className="text-blue-400">auth.protocol: tls1.3</div>
    </div>
  );
};

const BentoTile = ({ icon: Icon, title, desc, widget, className }: any) => (
  <Card className={cn("p-6 md:p-8 group relative hover-scan overflow-hidden flex flex-col justify-between", className)}>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Icon className="w-6 h-6 text-accent group-hover:scale-110 transition-transform" />
        <span className="text-[8px] font-black text-white/10 uppercase tracking-widest group-hover:text-accent/20 transition-colors">LensVerse</span>
      </div>
      <div>
        <h4 className="text-base md:text-lg font-bold text-white mb-1">{title}</h4>
        <p className="text-xs text-white/40 leading-relaxed">{desc}</p>
      </div>
    </div>
    <div className="mt-6 pt-4 border-t border-white/5">
      {widget}
    </div>
  </Card>
);

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="border border-white/10 rounded-xl overflow-hidden bg-deep-1"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 md:p-6 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <h3 className="text-base md:text-lg font-bold text-white pr-4 md:pr-8">{question}</h3>
        <ChevronDown
          className={cn(
            "w-5 h-5 text-accent transition-transform duration-300 flex-shrink-0",
            isOpen && "rotate-180"
          )}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0 }}
        className="overflow-hidden"
      >
        <p className="px-4 md:px-6 pb-4 md:pb-6 text-sm md:text-base text-white/60 leading-relaxed">{answer}</p>
      </motion.div>
    </motion.div>
  );
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { mission } = useStore();
  const [activeTab, setActiveTab] = useState('Banking');

  const industries = {
    Banking: "Secure loan processing, KYC automation, and fraud detection for modern banks.",
    Insurance: "Accelerate claims handling and automate policy underwriting with AI vision.",
    Government: "Digitize citizen services and archives with Arabic-first high-precision OCR.",
    SharedServices: "Scalable document routing and processing for global business centers."
  };

  const faqs = [
    {
      question: "What makes LensVerse different from other OCR solutions?",
      answer: "LensVerse is purpose-built for Arabic-first document processing. Our AI-powered OCR engine extracts text from Arabic and English documents with high confidence scoring, and includes built-in Arabic-to-English translation. The platform is designed for organizations in regulated sectors who need reliable, on-premise-capable document workflows."
    },
    {
      question: "Can LensVerse be deployed on-premises?",
      answer: "Yes! LensVerse supports on-premises deployment. The core platform (Django, PostgreSQL, Redis, Elasticsearch) runs entirely within your infrastructure. Note that cloud OCR providers (Gemini, OpenAI) are used for structured analysis by default, but can be replaced with self-hosted models."
    },
    {
      question: "What types of documents can LensVerse process?",
      answer: "LensVerse processes a wide range of document types including invoices, contracts, ID documents, forms, receipts, and multi-page financial documents. The platform supports both Arabic and English content, with configurable document type classification for your specific use cases."
    },
    {
      question: "How does document processing work?",
      answer: "Documents are uploaded through the dashboard or API, then processed by our OCR engine which extracts text and fields with per-field confidence scores. Results can be reviewed, translated from Arabic to English, and exported. Low-confidence extractions are flagged for manual review before finalization."
    },
    {
      question: "What security certifications does LensVerse have?",
      answer: "LensVerse encrypts sensitive fields at rest using Fernet symmetric encryption, maintains a full audit history of document and configuration changes, and enforces TLS 1.3 with HSTS for all connections. Authentication is handled via token-based auth with OTP support."
    },
    {
      question: "How do I get started with LensVerse?",
      answer: "Sign up to get started. Once onboarded, your team will receive access to the dashboard where you can upload documents, configure processing settings, and start extracting data immediately. Our team provides support during setup and integration with your existing systems."
    }
  ];


  const [currentIndex, setCurrentIndex] = useState(0);

  const slides = [
    {
      id: 0,
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop",
      title: (
        <>
          <span className="block whitespace-nowrap leading-[1.1] pb-1">AI-Powered Intelligent</span>
          <span className="bg-gradient-to-r from-accent to-gold text-transparent bg-clip-text italic block whitespace-nowrap leading-[1.1] pt-1 mt-1 pb-2">
            Document Processing.
          </span>
        </>
      ),
      subtitle: "Arabic-first document processing for regulated industries. Extract, classify, and translate documents with AI-powered OCR and on-premise deployment.",
      badges: [
        { text: "Arabic-First", variant: "accent", className: "bg-accent/10 border-accent/30 text-accent" },
        { text: "On-Prem Ready", variant: "default", className: "bg-white/5 border-white/10 opacity-60" },
        { text: "Confidence Scoring", variant: "info", className: "bg-blue-400/10 border-blue-400/30 text-blue-400" }
      ]
    },
    {
      id: 1,
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop",
      title: (
        <>
          <span className="block whitespace-nowrap leading-[1.1] pb-1">Enterprise Security.</span>
          <span className="bg-gradient-to-r from-blue-400 to-indigo-400 text-transparent bg-clip-text italic block whitespace-nowrap leading-[1.1] pt-1 mt-1 pb-2">
            Secure by Design.
          </span>
        </>
      ),
      subtitle: "Encrypted storage at rest, full audit history, and TLS 1.3 securing all data in transit.",
      badges: [
        { text: "Encrypted Storage", variant: "accent", className: "bg-blue-500/10 border-blue-500/30 text-blue-400" },
        { text: "TLS 1.3", variant: "default", className: "bg-white/5 border-white/10 opacity-60" },
        { text: "Audit Trail", variant: "info", className: "bg-indigo-400/10 border-indigo-400/30 text-indigo-400" }
      ]
    },
    {
      id: 2,
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2070&auto=format&fit=crop",
      title: (
        <>
          <span className="block whitespace-nowrap leading-[1.1] pb-1">Flexible Architecture.</span>
          <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 text-transparent bg-clip-text italic block whitespace-nowrap leading-[1.1] pt-1 mt-1 pb-2">
            Built to Adapt.
          </span>
        </>
      ),
      subtitle: "Multi-tenant architecture with cloud and on-premise deployment options. Integrate document processing into your workflows via our REST API.",
      badges: [
        { text: "Multi-Tenant", variant: "accent", className: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" },
        { text: "Cloud Ready", variant: "default", className: "bg-white/5 border-white/10 opacity-60" },
        { text: "API-Driven", variant: "info", className: "bg-cyan-400/10 border-cyan-400/30 text-cyan-400" }
      ]
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-deep-0 relative selection:bg-accent selection:text-white">
      <HUDOverlay />

      {/* Sticky Navigation */}
      <nav className="fixed top-0 w-full z-[110] glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-[32px] h-16 sm:h-[80px] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-3 cursor-pointer" onClick={() => {
            const el = document.getElementById('hero');
            el?.scrollIntoView({ behavior: 'smooth' });
          }}>
            <img
              src={LensVerseLogo}
              alt="LensVerse Logo"
              className="w-6 h-6 md:w-8 md:h-8 object-contain"
            />
            <span className="text-[19px] font-[700] leading-none tracking-tight text-white">LensVerse</span>
          </div>

          <div className="hidden lg:flex items-center gap-[36px]">
            {[
              { label: 'Platform', id: 'platform' },
              { label: 'Use Case', id: 'use-cases' },
              { label: 'Admin Console', id: 'mission-console' },
              { label: 'Security', id: 'security' },
              { label: 'FAQ', id: 'faq' }
            ].map(item => (
              <button key={item.label} onClick={() => {
                const el = document.getElementById(item.id);
                el?.scrollIntoView({ behavior: 'smooth' });
              }} className="text-[15px] font-[600] tracking-[0.04em] leading-none text-white/90 hover:text-white transition-opacity uppercase">{item.label}</button>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-4 md:gap-6">
            <button
              onClick={() => navigate('/auth/login')}
              className="text-[12px] sm:text-[15px] font-[600] tracking-[0.04em] leading-none text-white/90 hover:text-white transition-opacity uppercase whitespace-nowrap"
            >
              Sign In
            </button>
            <Button
              className="text-[13px] sm:text-[15px] font-[700] px-3 sm:px-[26px] py-[10px] sm:py-[14px] min-h-[40px] sm:min-h-[46px] rounded-[10px] whitespace-nowrap"
              onClick={() => navigate('/auth/signup')}
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Cinematic Hero */}
      <section id="hero" className="relative min-h-screen flex items-center px-6 md:px-8 pt-24 md:pt-20 overflow-hidden">
        {/* Background Images with AnimatePresence */}
        <AnimatePresence mode="popLayout">
          <motion.div
            key={slides[currentIndex].id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 z-0"
          >
            <img
              src={slides[currentIndex].image}
              alt="Background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-deep-0 via-deep-0/95 to-deep-0/40" />
          </motion.div>
        </AnimatePresence>

        <AgentNetwork />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-12 items-center w-full relative z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={slides[currentIndex].id}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.5 }}
              className="space-y-10 lg:col-span-7 xl:col-span-8"
            >
              <div className="inline-flex flex-wrap gap-3">
                {slides[currentIndex].badges.map((badge, idx) => (
                  <Badge key={idx} variant={badge.variant as any} className={cn("text-[13px] font-[600]", badge.className)}>{badge.text}</Badge>
                ))}
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-[5rem] lg:text-[4.5rem] xl:text-[5rem] font-black tracking-tighter leading-[1.05] text-white">
                {slides[currentIndex].title}
              </h1>

              <p className="text-[18px] leading-[1.5] text-white/40 max-w-[620px] font-medium">
                {slides[currentIndex].subtitle}
              </p>

              <div className="flex flex-wrap gap-4 md:gap-5 w-full">
                <Button size="xl" className="group relative overflow-hidden h-12 w-full md:w-auto" onClick={() => navigate('/auth/signup')}>
                  <span className="relative z-10">Get Started</span>
                  <div className="absolute inset-0 bg-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                  <ArrowRight className="ml-3 w-5 h-5 relative z-10" />
                </Button>
                <Button
                  variant="glass"
                  size="xl"
                  className="group w-full md:w-auto"
                  onClick={() => {
                    const el = document.getElementById('video-section');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Watch Demo <Play className="ml-3 w-5 h-5 fill-current group-hover:scale-110 transition-transform" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="relative h-[300px] sm:h-[400px] lg:h-full lg:min-h-[600px] flex items-center justify-center lg:col-span-5 xl:col-span-4 w-full">
            <AgentCoreVisual />
          </div>
        </div>

        {/* Carousel Dots */}
        <div className="absolute bottom-10 left-0 right-0 z-20 flex justify-center gap-3">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentIndex
                ? "bg-[#F76C1C] scale-125"
                : "bg-white/50 hover:bg-white/80"
                }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Trust Strip */}
      <div className="border-y border-white/5 py-8 md:py-12 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-wrap justify-center md:justify-between items-center gap-6 md:gap-12 opacity-30 grayscale contrast-125 text-center">
          {/* Changed text-accent to text-white below */}
          <div className="font-black text-sm md:text-xl tracking-tighter uppercase text-white w-[40%] md:w-auto">On-prem Sovereign</div>
          <div className="font-black text-sm md:text-xl tracking-tighter uppercase text-white w-[40%] md:w-auto">Secure Auth</div>
          <div className="font-black text-sm md:text-xl tracking-tighter uppercase text-white w-[40%] md:w-auto">Arabic NLP</div>
          <div className="font-black text-sm md:text-xl tracking-tighter uppercase text-white w-[40%] md:w-auto">Multi-Tenant</div>
        </div>
      </div>

      {/* Use Cases Section */}
      <motion.section
        {...fadeInUp}
        id="use-cases"
        className="py-16 md:py-32 px-6 md:px-8 relative z-10 bg-white/[0.01]"
      >
        <div className="max-w-7xl mx-auto space-y-12 md:space-y-20">
          <div className="text-center space-y-4 md:space-y-6">
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tighter uppercase leading-tight">Built for Regulated Hubs.</h2>
            <p className="text-lg md:text-xl text-white/40 max-w-2xl mx-auto font-medium">Designed for BFSI, Healthcare, and Government sectors.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Users2, title: 'KYC & Onboarding', desc: 'Instantly verify national IDs and passports to compress time-to-onboard.' },
              { icon: CreditCard, title: 'Credit Decisioning', desc: 'Accelerate loan approvals by extracting data from income proofs and bank statements.' },
              { icon: Briefcase, title: 'Trade Finance', desc: 'Process complex L/Cs and shipping documents while ensuring policy adherence.' },
              { icon: ShieldCheck, title: 'Claims Validation', desc: 'Speed up settlements by automating the intake and validation of medical claim forms.' },
              { icon: FileText, title: 'Account Payables', desc: 'Capture and validate invoice data automatically to streamline financial operations.' },
              { icon: History, title: 'Regulatory Audit', desc: 'Maintain 100% visibility into historical document processing for regulatory inspections.' }
            ].map((useCase, i) => (
              <motion.div key={i} whileHover={{ y: -8 }}>
                <Card className="p-12 h-full space-y-8 relative group overflow-hidden border-white/5 bg-deep-surface/40 backdrop-blur-xl transition-all duration-500">
                  <div className="flex items-center justify-between">
                    <useCase.icon className="w-10 h-10 text-accent opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="text-[10px] font-black text-white/10 uppercase tracking-[0.4em]">SOLUTION_{i + 1}</span>
                  </div>
                  <h4 className="text-2xl font-black text-white tracking-tighter uppercase leading-none">{useCase.title}</h4>
                  <p className="text-sm text-white/40 leading-relaxed font-medium">{useCase.desc}</p>
                  <div className="pt-6 border-t border-white/5">
                    <button
                      onClick={() => navigate('/auth/login')}
                      className="text-[10px] font-black text-accent uppercase tracking-[0.3em] hover:text-white transition-all flex items-center gap-3"
                    >
                      Explore solution <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Enhanced Product Bento Grid */}
      <section id="platform" className="py-16 md:py-32 px-6 md:px-8 max-w-7xl mx-auto space-y-12 md:space-y-20">
        <motion.div {...fadeInUp} className="text-center space-y-4">
          <h2 className="text-3xl md:text-5xl font-black text-white">Document Processing Hub</h2>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-6">
          {/* Main Large OCR Tile */}
          <Card className="md:col-span-2 md:row-span-2 p-8 md:p-10 group relative hover-scan bg-gradient-to-br from-white/[0.03] to-transparent">
            <div className="h-full flex flex-col justify-between">
              <div className="space-y-6">
                <Search className="w-10 h-10 text-accent" />
                <h3 className="text-3xl md:text-4xl font-black text-white">AI-Powered OCR</h3>
                <p className="text-white/40 text-base md:text-lg leading-relaxed">Arabic and English text extraction with per-field confidence scoring. Powered by PaddleOCR and GPU-accelerated processing.</p>
              </div>
              <div className="mt-12 space-y-4">
                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-white/20">
                  <span>Confidence Level</span>
                  <span className="text-accent">99.8%</span>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-accent shadow-[0_0_15px_#F76C1C]"
                    initial={{ width: 0 }}
                    whileInView={{ width: '99.8%' }}
                    transition={{ duration: 1.5 }}
                  />
                </div>
              </div>
            </div>
          </Card>

          <BentoTile
            icon={Layers}
            title="Classification"
            desc="Configurable document type detection and routing for your document workflows."
            widget={<MiniHUDWidget type="pulse" />}
          />

          <BentoTile
            icon={Globe2}
            title="Translation"
            desc="Arabic-to-English translation powered by AI agents and GPU processing."
            widget={<MiniHUDWidget type="log" />}
          />

          <BentoTile
            icon={Upload}
            title="Upload & Processing"
            desc="Drag-and-drop document upload with automated OCR processing pipeline."
            widget={<MiniHUDWidget type="gauge" />}
          />

          <BentoTile
            icon={Workflow}
            title="Admin Console"
            desc="Manage users, document types, email templates, and platform settings."
            widget={<MiniHUDWidget type="chart" />}
          />

          <BentoTile
            icon={Settings}
            title="Settings & API Keys"
            desc="Configure platform settings, generate API tokens, and manage integrations."
            widget={<MiniHUDWidget type="pulse" />}
          />

          <BentoTile
            icon={BarChart3}
            title="Dashboard & Analytics"
            desc="View processing statistics, document counts, and system status at a glance."
            widget={<MiniHUDWidget type="chart" />}
          />
        </div>
      </section>

      {/* Interactive Mission Console */}
      <AdminConsole />

      {/* Video Section */}
      <section id="video-section" className="py-16 md:py-32 px-6 md:px-8 max-w-7xl mx-auto">
        <motion.div {...fadeInUp} className="space-y-12 md:space-y-16">
          <div className="text-center space-y-4">
            <Badge variant="accent">Platform Demo</Badge>
            <h2 className="text-3xl md:text-5xl font-black text-white">See the Platform in Action</h2>
            <p className="text-white/40 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Watch how automated document processing transforms enterprise workflows in real-time.
            </p>
          </div>

          <Card className="p-4 md:p-8 bg-deep-1 border-accent/20 relative overflow-hidden group hover-scan">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="relative aspect-video rounded-xl overflow-hidden bg-black border border-white/10">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/dIfWfHljytY"
                title="LensVerse Platform Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />

              {/* Tactical overlay corners */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-accent pointer-events-none" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-accent pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-accent pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-accent pointer-events-none" />
            </div>
          </Card>
        </motion.div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-16 md:py-32 px-6 md:px-8 max-w-7xl mx-auto">
        <Card className="p-8 md:p-20 bg-deep-1 border-accent/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 blur-3xl -mr-32 -mt-32" />
          <div className="grid lg:grid-cols-2 gap-20">
            <div className="space-y-8">
              <Badge variant="accent">Mission Security</Badge>
              <h2 className="text-5xl font-black text-white leading-tight">Enterprise Security</h2>
              <p className="text-white/40 text-lg leading-relaxed font-medium">
                Sensitive fields encrypted at rest, full audit history on every change, and end-to-end TLS protecting data in transit.
              </p>
              <div className="grid grid-cols-2 gap-8">
                {[
                  { label: 'Encryption', val: 'Fernet / TLS 1.3' },
                  { label: 'Logs', val: 'Audit History' },
                  { label: 'Access', val: 'Token + OTP Auth' },
                  { label: 'Protocol', val: 'HTTPS + HSTS' }
                ].map(s => (
                  <div key={s.label} className="space-y-1">
                    <p className="text-[10px] font-black text-white/20 uppercase tracking-widest">{s.label}</p>
                    <p className="font-bold text-white">{s.val}</p>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/security')}
                className="border-white/20 text-white hover:bg-accent hover:border-accent hover:text-white transition-all duration-300"
              >
                Review Security Brief
              </Button>
            </div>
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-white/5 bg-deep-0 font-mono text-[10px] text-accent/60 h-80 overflow-hidden relative shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-deep-0 pointer-events-none" />
                {[...Array(20)].map((_, i) => (
                  <div key={i} className="mb-1">
                    <span className="opacity-30">[{new Date().toISOString()}]</span> LOG::AGENT_VERIFY_PROTOCOL_LINK_ESTABLISHED_{i}...
                  </div>
                ))}
              </div>
              <p className="text-center text-[10px] font-black uppercase tracking-[0.4em] text-white/10">Secure Log Sync</p>
            </div>
          </div>
        </Card>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-32 px-6 md:px-8 max-w-4xl mx-auto">
        <motion.div {...fadeInUp} className="space-y-8 md:space-y-12">
          <div className="text-center space-y-4">
            <Badge variant="accent">Information</Badge>
            <h2 className="text-3xl md:text-5xl font-black text-white">Frequently Asked Questions</h2>
            <p className="text-white/40 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about LensVerse and our automated document processing platform.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-10 md:py-16 px-6 md:px-8 border-t border-white/5 bg-deep-0 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-8">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img
              src={LensVerseLogo}
              alt="LensVerse Logo"
              className="w-7 h-7 object-contain"
            />
            <span className="text-lg font-black tracking-tight text-white">LensVerse</span>
          </div>

          {/* Links */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            <span onClick={() => navigate('/api-docs')} className="text-sm text-white/40 font-bold hover:text-accent transition-colors cursor-pointer">API Docs</span>
            <a href="https://www.youtube.com/@VikashanTechnologies" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 font-bold hover:text-accent transition-colors">YouTube</a>
            <a href="https://www.linkedin.com/company/vikashan-technologies/" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 font-bold hover:text-accent transition-colors">LinkedIn</a>
            <a href="mailto:info@vtuae.com" className="text-sm text-white/40 font-bold hover:text-accent transition-colors">Support</a>
          </div>

          {/* Copyright */}
          <div className="pt-6 border-t border-white/5 w-full text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-white/20">© 2026 LensVerse Technologies — Proudly built in the Arab World.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;